import * as E from "fp-ts/Either"

const BE_TO_FE_PROVIDER: Record<string, string> = {
  MAGIC_LINK: "EMAIL",
}

function mapToFeProviders(beProviders: string[]): string[] {
  return beProviders.map((p) => BE_TO_FE_PROVIDER[p] ?? p)
}

const FALLBACK_PROVIDERS = ["GITHUB", "GOOGLE", "MICROSOFT", "EMAIL"]

// Short TTL so an admin's auth-method change (provider toggle, SSO verify)
// surfaces on the sign-in screen promptly. The BE Redis cache is invalidated
// on every change, so this client cache only needs to collapse rapid reloads
// against the throttled by-host endpoint — 30s does that without making admin
// edits look "not respected" for minutes.
const CACHE_TTL_MS = 30 * 1000

type CachedAuthMethods = {
  methods: string[]
  samlEntryPoint?: string
  oidcInitiationUrl?: string
  ts: number
}

function readCache(key: string): CachedAuthMethods | null {
  const raw = sessionStorage.getItem(key)
  if (!raw) return null

  try {
    const parsed: unknown = JSON.parse(raw)

    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "methods" in parsed &&
      "ts" in parsed
    ) {
      const entry = parsed as CachedAuthMethods
      if (Date.now() - entry.ts > CACHE_TTL_MS) {
        sessionStorage.removeItem(key)
        return null
      }
      if (
        Array.isArray(entry.methods) &&
        entry.methods.every((v) => typeof v === "string")
      ) {
        return entry
      }
    }

    sessionStorage.removeItem(key)
    return null
  } catch {
    sessionStorage.removeItem(key)
    return null
  }
}

function writeCache(
  key: string,
  methods: string[],
  samlEntryPoint?: string,
  oidcInitiationUrl?: string
): void {
  const entry: CachedAuthMethods = {
    methods,
    samlEntryPoint,
    oidcInitiationUrl,
    ts: Date.now(),
  }
  sessionStorage.setItem(key, JSON.stringify(entry))
}

function parseByHostResponse(data: unknown): {
  ssoMode: string
  methods: string[]
  samlEntryPoint?: string
  oidcInitiationUrl?: string
} | null {
  if (typeof data !== "object" || data === null) return null
  const obj = data as Record<string, unknown>

  if (typeof obj.ssoMode !== "string") return null
  if (!Array.isArray(obj.methods)) return null

  const methods: string[] = []
  for (const entry of obj.methods) {
    if (typeof entry === "string") {
      methods.push(entry.toUpperCase())
    }
  }

  return {
    ssoMode: obj.ssoMode,
    methods,
    samlEntryPoint:
      typeof obj.samlEntryPoint === "string" ? obj.samlEntryPoint : undefined,
    oidcInitiationUrl:
      typeof obj.oidcInitiationUrl === "string"
        ? obj.oidcInitiationUrl
        : undefined,
  }
}

let lastSamlEntryPoint: string | undefined
let lastOidcInitiationUrl: string | undefined

export function getLastSamlEntryPoint(): string | undefined {
  return lastSamlEntryPoint
}

export function getLastOidcInitiationUrl(): string | undefined {
  return lastOidcInitiationUrl
}

export async function fetchOrgAuthProviders(
  host: string,
  backendApiUrl: string
): Promise<E.Either<string, string[]>> {
  lastSamlEntryPoint = undefined
  lastOidcInitiationUrl = undefined

  const cacheKey = `auth_methods_${host}`

  const cached = readCache(cacheKey)
  if (cached) {
    lastSamlEntryPoint = cached.samlEntryPoint
    lastOidcInitiationUrl = cached.oidcInitiationUrl
    return E.right(cached.methods)
  }

  try {
    const res = await fetch(
      `${backendApiUrl}/orgs/by-host/${encodeURIComponent(host)}/auth-methods`
    )

    if (!res.ok) {
      console.error(
        "[org-auth-methods] Non-OK response:",
        res.status,
        res.statusText
      )
      return E.right(FALLBACK_PROVIDERS)
    }

    const data: unknown = await res.json()
    const parsed = parseByHostResponse(data)

    if (
      !parsed ||
      (parsed.methods.length === 0 &&
        !parsed.samlEntryPoint &&
        !parsed.oidcInitiationUrl)
    ) {
      return E.right(FALLBACK_PROVIDERS)
    }

    lastSamlEntryPoint = parsed.samlEntryPoint
    lastOidcInitiationUrl = parsed.oidcInitiationUrl

    const feProviders = mapToFeProviders(parsed.methods)
    if (parsed.samlEntryPoint) {
      feProviders.push("SAML")
    }
    if (parsed.oidcInitiationUrl) {
      feProviders.push("OIDC")
    }
    writeCache(
      cacheKey,
      feProviders,
      parsed.samlEntryPoint,
      parsed.oidcInitiationUrl
    )

    return E.right(feProviders)
  } catch (e) {
    console.error("[org-auth-methods] Failed to fetch auth methods:", e)
    return E.right(FALLBACK_PROVIDERS)
  }
}
